import React, { useEffect, useState } from 'react';
import axios from 'axios';
import LoadingSpinner from './LoadingSpinner';
import { formatHistoryDateTime } from '../utils/visaApplicationHistoryApi';
import { formatStatusLabel, getStatusBadgeClass } from '../utils/visaApplicationStatus';

const VisaApplicationHistoryModal = ({ application, historyUrl, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!application?.id || !historyUrl) return;

    let cancelled = false;

    const fetchHistory = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await axios.get(historyUrl);
        if (!cancelled) {
          setHistory(res.data.history || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || 'Failed to load status history');
          setHistory([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchHistory();

    return () => {
      cancelled = true;
    };
  }, [application?.id, historyUrl]);

  if (!application) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Status History</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Passenger ID: {application.passenger_id}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {loading ? (
          <LoadingSpinner text="Loading history..." />
        ) : error ? (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {error}
          </div>
        ) : history.length === 0 ? (
          <p className="text-center text-gray-400 py-8">No status history recorded yet.</p>
        ) : (
          <div className="space-y-4">
            {history.map((entry, index) => (
              <div
                key={entry.id || index}
                className="border border-gray-100 rounded-xl p-4 bg-gray-50/50"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(entry.application_status)}`}
                  >
                    {formatStatusLabel(entry.application_status)}
                  </span>
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {formatHistoryDateTime(entry.created_at)}
                  </span>
                </div>
                {entry.status_description && (
                  <p className="text-sm text-gray-700 mt-3">{entry.status_description}</p>
                )}
                {entry.changed_by_name && (
                  <p className="text-xs text-gray-400 mt-2">
                    Updated by {entry.changed_by_name}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VisaApplicationHistoryModal;
